import styled from 'styled-components'
import { gold, black, textMuted } from '@/theme/colors'
import { media } from '@/theme/tokens'
import { text } from '@/theme/typography'
import { Button } from '@components/Button'

/**
 * Centers the zero-state in whatever space it is given: `flex: 1` claims a
 * flex column's free space, and the `page` min-height keeps it centered on the
 * pages that render it as a plain block. The `panel` variant drops that floor
 * so the zero-state fits inside an inset scroll panel.
 */
export const EmptyStateContainer = styled.div<{ $panel: boolean }>`
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: ${({ $panel }) => ($panel ? '0' : '60vh')};
	padding: ${({ $panel }) => ($panel ? '40px 20px' : '32px 24px')};
`

export const EmptyStateBlock = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	gap: 16px;
	max-width: 420px;
`

/** Gold line art — the zero-state accent shared with the overview tiles. */
export const EmptyStateArtwork = styled.div<{ $panel: boolean }>`
	color: ${gold};
	display: flex;
	align-items: center;
	justify-content: center;

	svg {
		width: ${({ $panel }) => ($panel ? '72px' : '112px')};
		height: ${({ $panel }) => ($panel ? '72px' : '112px')};
	}

	${media.phone} {
		svg {
			width: ${({ $panel }) => ($panel ? '64px' : '88px')};
			height: ${({ $panel }) => ($panel ? '64px' : '88px')};
		}
	}
`

export const EmptyStateTitle = styled.h2<{ $panel: boolean }>`
	${({ $panel }) => ($panel ? text.title : text.heading)}
	color: ${black};
	margin: 0;
`

export const EmptyStateDescription = styled.p`
	${text.body}
	color: ${textMuted};
	margin: 0;
`

/** Wraps the caller's CTAs so one or two buttons lay out identically. */
export const EmptyStateActions = styled.div`
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	gap: 12px;
	margin-top: 8px;
`

/** The zero-state CTA — the app Button at its large size. */
export const EmptyStateButton = styled(Button).attrs({ size: 'lg' as const })``

/** A quiet one-line zero-state for a table, list or dropdown: centered, muted, no art. */
export const EmptyNote = styled.p`
	${text.bodyXs}
	color: ${textMuted};
	text-align: center;
	margin: 0;
	padding: 32px 16px;
`

/** The inline form, for a detail card or a chart: left-aligned and tight. */
export const EmptyNoteInline = styled.p`
	${text.bodyXs}
	color: ${textMuted};
	margin: 0;
	padding: 8px 0;
`
